/**
 * authenticateWithDiscoverablePasskey — the ONE discoverable-credential passkey LOGIN ceremony.
 *
 * USER-FACING-GEMS L1 (2026-09-29). Six web apps signed in with a passkey through their own
 * copies of this sequence (five `usePasskeyAuth` hooks and pos-web's orchestrator), and they had
 * drifted into two readings of ONE server reply. `createPasskeyRouter`'s
 * `POST /discoverable/options` answers the flat envelope data
 * `{ ...publicKeyCredentialRequestOptions, challengeSessionId }`; five copies destructured a
 * nested `{ options, challengeSessionId }`, handed WebAuthn `undefined`, and failed every passkey
 * sign-in in palm, planner, patrol, payday and purchase-web — while their suites mocked the
 * nested shape and stayed green. pos-web read "nested, else flat", a hedge. This module decodes
 * the reply exactly once, in the shape the server writes.
 *
 * Sequence (FIDO discoverable flow — empty `allowCredentials`, the server identifies the user by
 * the credential id):
 *   1. WebAuthn support gate — before any request, so an unsupported browser never mints a
 *      server challenge it cannot answer.
 *   2. `authService.getPasskeyDiscoverableOptions(companyId)` → `{ challengeSessionId, ...options }`.
 *   3. `getPasskeyAssertion` — `webAuthnService.getCredential(prepareAuthenticationOptions(options))`, formatted; the
 *      options pass through untouched: the server is the authority on `userVerification`, `timeout` and `rpId`.
 *   4. `authService.authenticateWithPasskey(formattedCredential, companyId, challengeSessionId)`.
 *
 * It returns whatever the verify leg returns. That is deliberate: the tenant apps bind
 * `authenticateWithPasskey` to auth-client's context projection (stores the tokens, answers
 * `{ user, locations, accessToken }`), pos-web to the raw leg its own login hook installs.
 *
 * Browser-side failures throw a coded error (`PASSKEY_LOGIN_ERROR_CODES`); server failures
 * propagate unchanged in the fleet error vocabulary (`status` / `errorCode`).
 *
 * @module @puffin-core/login-shell/utils/authenticateWithDiscoverablePasskey
 */

import { PASSKEY_LOGIN_ERROR_CODES, passkeyLoginError } from './passkeyLoginErrors.js';

/**
 * DOMException names the WebAuthn `get()` rejects with when the user dismisses the prompt, the
 * prompt times out, or the page aborts it. The spec deliberately does not distinguish a dismissal
 * from "no matching credential" (privacy), so all of them read as a cancellation.
 */
const CANCELLATION_ERROR_NAMES = Object.freeze(['NotAllowedError', 'AbortError']);

/**
 * Ask the authenticator for an assertion, classifying a dismissal as a cancellation, and answer it as the JSON a
 * verifier reads (`formatAuthenticationResponse`). The ONE browser half of every passkey check: this module's sign-in,
 * and pos-web's station unlock — whose challenge is the server's when it is reachable and the register's own when it
 * is not (POS-OFFLINE-STATION-LOCK-001 U14'd-2).
 *
 * @param {Object} webAuthnService - `@puffin-core/mfa-ui`'s `WebAuthnService`.
 * @param {Object} options - Request options (base64url fields), passed through untouched.
 * @returns {Promise<Object>} The formatted assertion.
 * @throws {Error} `WEBAUTHN_CANCELLED` for a dismissal or no passkey chosen; any other failure unchanged.
 */
export async function getPasskeyAssertion(webAuthnService, options) {
  let assertion;
  try {
    assertion = await webAuthnService.getCredential(
      webAuthnService.prepareAuthenticationOptions(options),
    );
  } catch (err) {
    if (err && CANCELLATION_ERROR_NAMES.includes(err.name)) {
      throw passkeyLoginError(
        PASSKEY_LOGIN_ERROR_CODES.WEBAUTHN_CANCELLED,
        'Passkey prompt dismissed',
        err,
      );
    }
    throw err;
  }
  if (!assertion) {
    throw passkeyLoginError(PASSKEY_LOGIN_ERROR_CODES.WEBAUTHN_CANCELLED, 'No passkey selected');
  }
  return webAuthnService.formatAuthenticationResponse(assertion);
}

/**
 * Run the discoverable passkey sign-in ceremony.
 *
 * @param {Object} params
 * @param {Object} params.authService - Exposes `getPasskeyDiscoverableOptions(companyId)` and
 *   `authenticateWithPasskey(credential, companyId, challengeSessionId)`.
 * @param {string} params.companyId - The tenant the sign-in is for (from subdomain routing).
 * @param {Object} params.webAuthnService - A `@puffin-core/mfa-ui` `WebAuthnService`.
 * @returns {Promise<*>} The verify leg's result.
 * @throws {Error} coded `WEBAUTHN_UNSUPPORTED` / `WEBAUTHN_CANCELLED`, or the auth-client error of
 *   a failed request.
 */
export async function authenticateWithDiscoverablePasskey({
  authService,
  companyId,
  webAuthnService,
}) {
  if (!(await webAuthnService.isSupported())) {
    throw passkeyLoginError(
      PASSKEY_LOGIN_ERROR_CODES.WEBAUTHN_UNSUPPORTED,
      'WebAuthn is not supported in this browser',
    );
  }

  const { challengeSessionId, ...options } =
    await authService.getPasskeyDiscoverableOptions(companyId);

  const assertion = await getPasskeyAssertion(webAuthnService, options);

  return authService.authenticateWithPasskey(assertion, companyId, challengeSessionId);
}

export default authenticateWithDiscoverablePasskey;
